import { CartAddEvent, CartErrorEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/** Whole amounts without cents ("$45.00" -> "$45"), like Liquid's money_without_trailing_zeros. */
const nvRound = (formatted) => String(formatted).replace(/([.,])00(?!\d)/, '');

/** Percentage of the running offer (snippets/nv-promo.liquid adds data-promo-pct/-ends), or 0 once it has ended. */
function nvPromoPct(element) {
  const pct = Number(element?.dataset.promoPct) || 0;
  const ends = Date.parse(element?.dataset.promoEnds || '');
  return pct > 0 && (Number.isNaN(ends) || Date.now() < ends) ? pct : 0;
}


/**
 * POST to /cart/add.js and let Horizon's cart drawer and bubble update.
 * @param {FormData} formData
 * @param {HTMLElement} source
 */
async function nvAddToCart(formData, source) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));
  const response = await fetch((window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js', {
    method: 'POST',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: formData,
  });
  const data = await response.json();
  if (data.status) return { error: data };
  const cart = await fetch('/cart.js').then((res) => res.json()).catch(() => undefined);
  source.dispatchEvent(
    new CartAddEvent(cart, String(formData.get('id')), {
      source: 'product-form-component',
      itemCount: Number(formData.get('quantity')) || 1,
      productId: source.dataset.productId,
      sections: data.sections,
    })
  );
  if (!document.querySelector('nv-cart-drawer, cart-drawer-component')) window.location.href = '/cart';
  return { data };
}

/**
 * Horizontal scroll-snap carousel with prev/next buttons.
 * Markup: <nv-carousel> with [data-carousel-track] and optional [data-carousel-prev]/[data-carousel-next].
 */
class NvCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;
    this.prev = this.querySelectorAll('[data-carousel-prev]');
    this.next = this.querySelectorAll('[data-carousel-next]');
    this.prev.forEach((button) => button.addEventListener('click', () => this.step(-1)));
    this.next.forEach((button) => button.addEventListener('click', () => this.step(1)));
    this.track.addEventListener('scroll', () => this.update(), { passive: true });
    window.addEventListener('resize', () => this.update(), { passive: true });
    this.update();
  }

  step(direction) {
    const slide = this.track.firstElementChild;
    if (!slide) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.track.scrollBy({ left: direction * (slide.getBoundingClientRect().width + gap), behavior: 'smooth' });
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft >= max;
    this.prev.forEach((button) => (button.disabled = atStart));
    this.next.forEach((button) => (button.disabled = atEnd));
    const hideNav = max <= 0;
    this.querySelectorAll('.nv-carousel__nav').forEach((nav) => (nav.style.visibility = hideNav ? 'hidden' : ''));
    const bar = this.querySelector('[data-carousel-progress]');
    if (bar) {
      const visible = Math.min(1, this.track.clientWidth / Math.max(1, this.track.scrollWidth));
      bar.style.width = `${visible * 100}%`;
      bar.style.transform = `translateX(${(this.track.scrollLeft / Math.max(1, this.track.clientWidth)) * 100}%)`;
      bar.parentElement.style.visibility = hideNav ? 'hidden' : '';
    }
  }
}

/**
 * Product page: gallery, bundle quantity, subscription option, live price and add to cart.
 */
class NvProduct extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[data-product-form]');
    this.tiers = (this.dataset.tiers || '0,0,0').split(',').map(Number);
    this.days = Number(this.dataset.days) || 0;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    this.quantity = 1;

    this.setupGallery();

    const variantSelect = this.querySelector('[data-variant-select]');
    variantSelect?.addEventListener('change', () => this.onVariantChange(variantSelect));
    this.variantPrice = this.readPrice(variantSelect);

    this.querySelectorAll('[data-bundle]').forEach((button) => {
      button.addEventListener('click', () => {
        this.quantity = Number(button.dataset.bundle) || 1;
        this.querySelectorAll('[data-bundle]').forEach((other) =>
          other.setAttribute('aria-checked', String(other === button))
        );
        this.render();
      });
    });

    this.querySelectorAll('[data-purchase]').forEach((radio) => radio.addEventListener('change', () => this.render()));
    // picking a delivery frequency, or clicking anywhere in the subscribe card, means "subscribe"
    const subscribe = this.querySelector('[data-purchase="subscribe"]');
    this.querySelectorAll('[data-plan-option]').forEach((option) =>
      option.addEventListener('change', () => {
        if (subscribe) subscribe.checked = true;
        this.render();
      })
    );
    this.querySelector('.nv-purchase__option--sub')?.addEventListener('click', (event) => {
      if (!subscribe || subscribe.checked || (event.target instanceof Element && event.target.closest('input, label'))) return;
      subscribe.checked = true;
      this.render();
    });

    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.setupSticky();
    this.render();
  }

  readPrice(select) {
    const option = select?.selectedOptions[0];
    if (option) return { price: Number(option.dataset.price), compare: Number(option.dataset.compare) || 0 };
    const priceText = this.querySelector('[data-your-price-value]');
    return { price: Number(priceText?.dataset.cents) || this.initialPrice(), compare: 0 };
  }

  initialPrice() {
    const json = this.querySelector('[data-variant-input]');
    return Number(json?.dataset.price) || 0;
  }

  onVariantChange(select) {
    const option = select.selectedOptions[0];
    this.querySelector('[data-variant-input]').value = option.value;
    this.variantPrice = this.readPrice(select);
    const available = option.dataset.available === 'true';
    const button = this.querySelector('[data-add-button]');
    if (button) button.disabled = !available;
    this.render();
  }

  /** The checked delivery frequency (one radio per selling plan), or null. */
  planOption() {
    return this.querySelector('[data-plan-option]:checked') || this.querySelector('[data-plan-option]');
  }

  planAdjustment() {
    const subscribe = this.querySelector('[data-purchase="subscribe"]');
    const planInput = this.querySelector('[data-plan-input]');
    const option = this.planOption();
    if (!subscribe || !subscribe.checked || !option) {
      if (planInput) planInput.value = '';
      return { type: null, value: 0 };
    }
    if (planInput) planInput.value = option.value || '';
    return { type: option.dataset.adjustType || null, value: Number(option.dataset.adjust) || 0 };
  }

  render() {
    const base = this.variantPrice.price;
    const adjustment = this.planAdjustment();
    const subscribing = Boolean(adjustment.type);

    // bundle tiers are a one-time offer; a subscription is one unit per delivery
    // the bundle tiers add nothing while an offer runs (Shopify applies the better discount, they don't stack)
    const promoRunning = nvPromoPct(this) > 0;
    this.querySelectorAll('[data-once-only]').forEach(
      (element) => (element.hidden = subscribing || (promoRunning && element.hasAttribute('data-promo-hide')))
    );
    if (promoRunning && this.quantity !== 1 && !subscribing) {
      this.quantity = 1;
      this.querySelectorAll('[data-bundle]').forEach((button) =>
        button.setAttribute('aria-checked', String(button.dataset.bundle === '1'))
      );
    }
    if (subscribing && this.quantity !== 1) {
      this.quantity = 1;
      this.querySelectorAll('[data-bundle]').forEach((button) =>
        button.setAttribute('aria-checked', String(button.dataset.bundle === '1'))
      );
    }
    const quantity = this.quantity;
    // Bundle discounts only apply to one-time purchases (the automatic discounts exclude subscriptions).
    const tier = adjustment.type ? 0 : this.tiers[quantity - 1] || 0;
    let unitPrice = base;

    if (adjustment.type === 'percentage') unitPrice = Math.round(unitPrice * (1 - adjustment.value / 100));
    if (adjustment.type === 'price') unitPrice = adjustment.value;
    if (adjustment.type === 'fixed_amount') unitPrice = Math.max(0, unitPrice - adjustment.value);

    const total = Math.round(unitPrice * quantity * (1 - tier / 100));
    const full = base * quantity;

    const quantityInput = this.querySelector('[data-quantity-input]');
    if (quantityInput) quantityInput.value = String(quantity);

    // subscribe card: price per delivery and the saving over a year for the chosen frequency
    const planOption = this.planOption();
    let perDelivery = base;
    if (planOption?.dataset.adjustType === 'percentage') {
      perDelivery = Math.round(base * (1 - Number(planOption.dataset.adjust) / 100));
      const subPrice = this.querySelector('[data-sub-price]');
      if (subPrice) subPrice.textContent = this.money(perDelivery);
      const subWas = this.querySelector('[data-sub-was]');
      if (subWas) subWas.textContent = this.money(base);
      const yearly = this.querySelector('[data-sub-yearly]');
      const months = Number(planOption.dataset.months) || 1;
      if (yearly?.dataset.template) {
        yearly.textContent = yearly.dataset.template.replace('[amount]', this.money(Math.round(((base - perDelivery) * 12) / months)));
      }
    }
    const oncePrice = this.querySelector('[data-once-price]');
    if (oncePrice) oncePrice.textContent = this.money(base);

    // the button and the note under it say exactly what happens; the button also shows the price (set below)
    const button = this.querySelector('[data-add-button]');
    const label = this.querySelector('[data-add-label]');
    const baseLabel = button && !button.disabled ? (subscribing ? button.dataset.labelSub : button.dataset.labelOnce) || '' : '';
    const renew = this.querySelector('[data-renew]');
    if (renew) {
      renew.hidden = !subscribing;
      if (subscribing && renew.dataset.template && planOption) {
        renew.textContent = renew.dataset.template
          .replace('[frequency]', planOption.dataset.frequency || '')
          .replace('[price]', this.money(perDelivery));
      }
    }
    const shipNote = this.querySelector('[data-ship-note]');
    if (shipNote) shipNote.textContent = (subscribing && shipNote.dataset.sub) || shipNote.dataset.once || shipNote.textContent;

    // running offer: Shopify applies the better of the bundle discount and the offer (they don't stack);
    // on a subscription it only takes 30% off the first delivery, renewals keep the plan price
    const promo = nvPromoPct(this);
    let pay = total;
    if (promo > 0) pay = subscribing ? Math.round(total * (1 - promo / 100)) : Math.round(full * (1 - Math.max(tier, promo) / 100));
    const promoLine = this.querySelector('[data-promo-line]');
    if (promoLine) {
      promoLine.hidden = !promo;
      if (promo) promoLine.textContent = (subscribing ? promoLine.dataset.sub : promoLine.dataset.once) || promoLine.textContent;
    }

    const value = this.querySelector('[data-your-price-value]');
    const was = this.querySelector('[data-your-price-was]');
    if (value) value.textContent = this.money(pay);
    if (was) {
      was.hidden = pay >= full;
      was.textContent = this.money(full);
    }
    if (label && baseLabel) label.textContent = `${baseLabel} – ${this.money(pay)}`;

    const perDay = this.querySelector('[data-per-day-value]');
    if (perDay && this.days > 0) perDay.textContent = this.money(Math.round(total / (this.days * quantity)));

    // sticky bar (mobile): same price and label as the main button
    const sticky = this.querySelector('[data-sticky-buy]');
    if (sticky) {
      const stickyPrice = sticky.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = this.money(pay);
      const stickyWas = sticky.querySelector('[data-sticky-was]');
      if (stickyWas) {
        stickyWas.hidden = pay >= full;
        stickyWas.textContent = this.money(full);
      }
      const stickyLabel = sticky.querySelector('[data-sticky-label]');
      // the bar shows the price next to the button, so the button keeps its plain label
      if (stickyLabel && label) stickyLabel.textContent = baseLabel || label.textContent;
    }
  }

  /** Mobile: a bar with price and button stays at the bottom while the main button is off screen. */
  setupSticky() {
    const bar = this.querySelector('[data-sticky-buy]');
    const main = this.querySelector('[data-add-button]');
    if (!bar || !main || !('IntersectionObserver' in window)) return;
    const button = bar.querySelector('[data-sticky-add]');
    button?.addEventListener('click', () => {
      if (main.disabled) return;
      if (this.form?.requestSubmit) this.form.requestSubmit(main);
      else main.click();
    });
    new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      if (button) button.tabIndex = show ? 0 : -1;
    }).observe(main);
  }

  money(cents) {
    try {
      return nvRound(formatMoney(cents, this.moneyFormat, this.currency));
    } catch (error) {
      return nvRound((cents / 100).toFixed(2));
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    const button = this.querySelector('[data-add-button]');
    const error = this.querySelector('[data-add-error]');
    if (!this.form || !button || button.disabled) return;

    const formData = new FormData(this.form);
    if (!formData.get('selling_plan')) formData.delete('selling_plan');
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (error) error.hidden = true;
    try {
      const result = await nvAddToCart(formData, this);
      if (result.error) {
        if (error) {
          error.textContent = result.error.description || result.error.message;
          error.hidden = false;
        }
        this.dispatchEvent(new CartErrorEvent(this.form.id || '', result.error.message, result.error.description, result.error.errors));
      }
    } catch (err) {
      this.form.submit();
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  setupGallery() {
    const track = this.querySelector('[data-gallery-track]');
    if (!track) return;
    const controls = this.querySelectorAll('[data-gallery-go]');
    const setCurrent = (index) => {
      controls.forEach((control) =>
        control.setAttribute('aria-current', String(Number(control.dataset.galleryGo) === index))
      );
    };
    controls.forEach((control) =>
      control.addEventListener('click', () => {
        const index = Number(control.dataset.galleryGo);
        track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
        setCurrent(index);
      })
    );
    let frame;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => setCurrent(Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
      },
      { passive: true }
    );
  }
}

/**
 * Wellness quiz: steps, optional email capture through the customer form, and results per goal.
 */
const NV_QUIZ_KEY = 'nv-quiz-results';

class NvQuiz extends HTMLElement {
  connectedCallback() {
    this.total = Number(this.dataset.total) || 0;
    this.history = [];
    this.goal = '';
    // ads can link straight to a goal (?goal=sleep): the first question is then already answered
    const preset = new URLSearchParams(window.location.search).get('goal');
    const goals = [...this.querySelectorAll('[data-quiz-step="1"] [data-quiz-answer]')].map((button) => (button.dataset.keys || '').split(',')[0].trim());
    this.querySelector('[data-quiz-start]')?.addEventListener('click', () => {
      if (preset && goals.includes(preset)) {
        this.goal = preset;
        this.answers[1] = [preset];
        this.go('2');
      } else {
        this.go('1');
      }
    });
    this.querySelectorAll('[data-quiz-back]').forEach((button) =>
      button.addEventListener('click', () => this.go(this.history.pop() || 'intro', false))
    );
    this.answers = {};
    this.querySelectorAll('[data-quiz-answer]').forEach((button) =>
      button.addEventListener('click', () => {
        const step = Number(button.closest('[data-quiz-step]').dataset.quizStep);
        const keys = (button.dataset.keys || '').split(',').map((key) => key.trim()).filter(Boolean);
        this.answers[step] = keys;
        // the first question decides the goal; later answers only fine-tune the ritual
        if (step === 1) this.goal = keys[0] || '';
        this.go(step >= this.total ? 'email' : String(step + 1));
      })
    );
    this.querySelector('[data-quiz-skip]')?.addEventListener('click', () => this.showResults(false));
    // Shopify's spam protection submits this form natively (a full page load), so the answers are kept
    // for the page Shopify sends the visitor back to, where restoreResults() shows them again.
    const form = this.querySelector('form[data-quiz-email]');
    form?.addEventListener('submit', () => {
      const goalInput = form.querySelector('[data-quiz-goal-input]');
      if (goalInput) goalInput.value = this.goal;
      const state = JSON.stringify({ g: this.goal, a: this.answers, t: Date.now() });
      try {
        sessionStorage.setItem(NV_QUIZ_KEY, state);
      } catch (error) {
        /* storage can be blocked; the URL fragment below carries the answers too */
      }
      const action = new URL(form.getAttribute('action') || '/contact', window.location.origin);
      action.hash = `${NV_QUIZ_KEY}=${encodeURIComponent(state)}`;
      form.setAttribute('action', action.pathname + action.search + action.hash);
      form.querySelector('button[type="submit"]')?.setAttribute('aria-busy', 'true');
      // without a page load (for example a blocked script), show the results here
      window.setTimeout(() => this.showResults(true), 5000);
    });
    this.restoreResults();
  }

  /** Back from the email signup: show the results for the answers saved on submit (at most 10 minutes old). */
  restoreResults() {
    const fromHash = window.location.hash.match(new RegExp(`${NV_QUIZ_KEY}=([^&]+)`));
    let state = null;
    try {
      state = JSON.parse(fromHash ? decodeURIComponent(fromHash[1]) : sessionStorage.getItem(NV_QUIZ_KEY) || 'null');
      sessionStorage.removeItem(NV_QUIZ_KEY);
    } catch (error) {
      state = null;
    }
    if (fromHash) history.replaceState(null, '', window.location.pathname + window.location.search);
    if (!state || Date.now() - (Number(state.t) || 0) > 10 * 60 * 1000) return;
    this.goal = state.g || '';
    this.answers = state.a || {};
    this.showResults(true);
  }

  go(step, record = true) {
    const current = this.querySelector('[data-quiz-step]:not([hidden])');
    if (record && current) this.history.push(current.dataset.quizStep);
    this.querySelectorAll('[data-quiz-step]').forEach((element) => (element.hidden = element.dataset.quizStep !== step));
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  showResults(withCode) {
    this.go('results');
    const code = this.querySelector('[data-quiz-code]');
    if (code) code.hidden = !withCode;
    const bundles = this.rankBundles();
    const tiers = this.querySelector('[data-quiz-tiers]');
    const template = this.querySelector('template[data-quiz-tier-template]');
    const results = [...this.querySelectorAll('[data-quiz-result]')];
    if (bundles.length && tiers && template) {
      this.renderMatch(bundles[0]);
      tiers.replaceChildren(...this.buildTiers(bundles, template));
      results.forEach((element) => (element.hidden = true));
      return;
    }
    this.renderMatch(null);
    const match = results.find((element) => element.dataset.quizResult === this.goal) || results[0];
    results.forEach((element) => (element.hidden = element !== match));
  }

  /**
   * The single best product for the answers (the first product of the best-matching ritual), shown above
   * the ritual options: one-time purchase first, Subscribe & save as the second choice.
   */
  renderMatch(bundle) {
    const box = this.querySelector('[data-quiz-match]');
    const template = this.querySelector('template[data-quiz-match-template]');
    const item = bundle?.querySelector('[data-item]');
    if (!box) return;
    if (!template || !item) {
      box.hidden = true;
      return;
    }
    const labels = template.dataset;
    const card = template.content.firstElementChild.cloneNode(true);
    const href = item.querySelector('a')?.getAttribute('href') || '#';
    card.querySelectorAll('[data-match-link]').forEach((link) => link.setAttribute('href', href));
    const image = item.querySelector('img');
    if (image) card.querySelector('.nv-quiz-match__img')?.append(image.cloneNode(true));
    const name = card.querySelector('[data-match-name]');
    if (name) name.textContent = item.querySelector('.nv-quiz-bundle__name')?.textContent.trim() || '';
    const badge = card.querySelector('[data-match-badge]');
    if (badge && labels.badge) {
      badge.textContent = labels.badge;
      badge.hidden = false;
    }

    // one product: no bundle discount; a running offer takes its percentage off (on a subscription the first delivery)
    const price = Number(item.dataset.price) || 0;
    const promo = nvPromoPct(this);
    const once = promo ? Math.round((price * (100 - promo)) / 100) : price;
    const priceEl = card.querySelector('[data-match-price]');
    if (priceEl) priceEl.textContent = this.money(once);
    const was = card.querySelector('[data-match-was]');
    if (was && once < price) {
      was.textContent = this.money(price);
      was.hidden = false;
    }
    const note = card.querySelector('[data-match-note]');
    if (note && promo) {
      note.textContent = `${this.dataset.promoName || 'Offer'}: ${promo}% off`;
      note.hidden = false;
    }
    const add = card.querySelector('[data-match-add]');
    if (add) {
      add.dataset.bundleAdd = item.dataset.variant || '';
      add.textContent = `${labels.add || 'Add to bag'} – ${this.money(once)}`;
    }
    const subPct = Number(item.dataset.pct) || 0;
    const sub = card.querySelector('[data-match-sub]');
    const subText = promo ? labels.subPromo : labels.sub;
    if (sub && subText && subPct > 0 && item.dataset.plan) {
      const regular = Math.round((price * (100 - subPct)) / 100);
      const first = promo ? Math.round((regular * (100 - promo)) / 100) : regular;
      sub.textContent = subText
        .replace('[pct]', String(subPct))
        .replace('[first]', this.money(first))
        .replace('[regular]', this.money(regular));
      sub.dataset.bundleAdd = item.dataset.variant || '';
      sub.dataset.bundlePlans = item.dataset.plan;
      sub.hidden = false;
    }
    const view = card.querySelector('[data-match-view]');
    if (view) {
      view.textContent = labels.view || '';
      view.hidden = !labels.view;
    }
    box.replaceChildren(card);
    box.hidden = false;
  }

  /**
   * Three options for the best-matching ritual: the full ritual (recommended), its core products,
   * and the ritual plus the first new product from the next matches. Desktop shows them small to big.
   */
  buildTiers(bundles, template) {
    const main = bundles[0];
    const labels = template.dataset;
    const core = [...main.querySelectorAll('[data-item]')];
    const handles = new Set(core.map((item) => item.dataset.handle));
    let extra = null;
    for (const bundle of bundles.slice(1)) {
      extra = [...bundle.querySelectorAll('[data-item]')].find((item) => !handles.has(item.dataset.handle)) || null;
      if (extra) break;
    }
    const options = [
      {
        items: core,
        order: 2,
        recommended: true,
        badge: labels.tier2Badge,
        title: main.querySelector('h3')?.textContent.trim() || '',
        text: main.querySelector('.nv-quiz-bundle__text')?.textContent.trim() || '',
      },
    ];
    if (core.length > 1) options.push({ items: core.slice(0, -1), order: 1, title: labels.tier1Title, text: labels.tier1Text });
    if (extra) {
      const name = extra.querySelector('.nv-quiz-bundle__name')?.textContent.trim() || '';
      options.push({ items: [...core, extra], order: 3, badge: labels.tier3Badge, title: labels.tier3Title, text: (labels.tier3Text || '').replace('[product]', name) });
    }
    return options.map((option) => this.tierCard(template, option));
  }

  tierCard(template, option) {
    const labels = template.dataset;
    const card = template.content.firstElementChild.cloneNode(true);
    const set = (selector, value) => card.querySelectorAll(selector).forEach((element) => (element.textContent = value));
    card.classList.toggle('nv-quiz-bundle--main', Boolean(option.recommended));
    card.style.setProperty('--nv-tier-order', String(option.order));
    const badge = card.querySelector('[data-tier-badge]');
    if (badge && option.badge) {
      badge.textContent = option.badge;
      badge.hidden = false;
    }
    set('[data-tier-title]', option.title || '');
    set('[data-tier-text]', option.text || '');
    const list = card.querySelector('[data-tier-items]');
    option.items.forEach((item) => list?.append(item.cloneNode(true)));

    // same maths as the cart: bundle discount by item count (one-time), plan discount per line (subscription)
    const prices = option.items.map((item) => Number(item.dataset.price) || 0);
    const full = prices.reduce((sum, price) => sum + price, 0);
    const count = option.items.length;
    const bundlePct = count >= 3 ? Number(this.dataset.pctThree) || 0 : count === 2 ? Number(this.dataset.pctTwo) || 0 : 0;
    // running offer: the better of bundle discount and offer (no stacking); on subscriptions only the first delivery
    const promo = nvPromoPct(this);
    const pct = Math.max(bundlePct, promo);
    const once = Math.round((full * (100 - pct)) / 100);
    const plans = option.items.map((item) => item.dataset.plan || '');
    const subPct = Number(option.items[0]?.dataset.pct) || 0;
    const subRegular = prices.reduce((sum, price) => sum + Math.round((price * (100 - subPct)) / 100), 0);
    const sub = promo ? Math.round((subRegular * (100 - promo)) / 100) : subRegular;

    set('[data-tier-full]', this.money(full));
    set('[data-tier-once-price]', this.money(once));
    const onceNote = promo && promo >= bundlePct ? labels.promoOnceNote || labels.onceNote : labels.onceNote;
    set('[data-tier-once-note]', pct ? (onceNote || '').replace('[amount]', this.money(full - once)).replace('[pct]', String(pct)) : labels.onceSingle || '');
    const onceWas = card.querySelector('[data-tier-once-price]')?.parentElement?.querySelector('[data-tier-full]');
    if (onceWas) onceWas.hidden = !pct;
    set('[data-tier-sub-price]', this.money(sub));
    const subNote = promo && labels.promoSubNote ? labels.promoSubNote.replace('[regular]', this.money(subRegular)) : labels.subNote || '';
    set('[data-tier-sub-note]', subNote.replace('[pct]', String(subPct)));
    const subBlock = card.querySelector('[data-tier-sub]');
    if (subBlock) subBlock.hidden = !(subPct > 0 && plans.every(Boolean) && sub <= once);

    const ids = option.items.map((item) => item.dataset.variant).join(',');
    card.querySelectorAll('[data-bundle-add]').forEach((button) => (button.dataset.bundleAdd = ids));
    const subButton = subBlock?.querySelector('[data-bundle-add]');
    if (subButton) subButton.dataset.bundlePlans = plans.join(',');
    return card;
  }

  money(cents) {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return nvRound(formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', currency));
    } catch (error) {
      return nvRound((cents / 100).toFixed(2));
    }
  }

  /** Bundles for the chosen goal first, then by how many answer keys they share. */
  rankBundles() {
    const keys = new Set(Object.values(this.answers || {}).flat());
    const pool = [...this.querySelectorAll('[data-bundle]')];
    const scored = pool.map((bundle, index) => {
      const own = (bundle.dataset.keys || '').split(',').map((key) => key.trim()).filter(Boolean);
      const shared = own.filter((key) => keys.has(key)).length;
      const goal = bundle.dataset.goal === this.goal ? 100 : 0;
      return { bundle, score: goal + shared * 10 - index * 0.01 };
    });
    const sameGoal = scored.filter((item) => item.score >= 100);
    const list = (sameGoal.length ? sameGoal : scored).sort((a, b) => b.score - a.score);
    return list.map((item) => item.bundle);
  }
}


/**
 * Quiz bundles: add every product of the ritual in one request. One-time: the bundle discount applies
 * automatically. With data-bundle-plans each product is added as a subscription on its own plan.
 */
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-bundle-add]') : null;
  if (!button || button.getAttribute('aria-busy') === 'true') return;
  const ids = button.dataset.bundleAdd.split(',').map(Number).filter(Boolean);
  if (!ids.length) return;
  const plans = (button.dataset.bundlePlans || '').split(',').map(Number);
  button.setAttribute('aria-busy', 'true');
  try {
    const items = ids.map((id, index) => (plans[index] ? { id, quantity: 1, selling_plan: plans[index] } : { id, quantity: 1 }));
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message);
    const cart = await fetch('/cart.js').then((res) => res.json()).catch(() => undefined);
    button.dispatchEvent(new CartAddEvent(cart, String(ids[0]), { source: 'nv-quiz-bundle', itemCount: ids.length }));
    if (!document.querySelector('nv-cart-drawer, cart-drawer-component')) window.location.href = '/cart';
  } catch (error) {
    window.location.href = '/cart';
  } finally {
    button.removeAttribute('aria-busy');
  }
});

/** Quick add buttons on product cards (single-variant products). */
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-quick-add]') : null;
  if (!button) return;
  event.preventDefault();
  if (button.getAttribute('aria-busy') === 'true') return;
  button.setAttribute('aria-busy', 'true');
  const formData = new FormData();
  formData.set('id', button.dataset.quickAdd);
  formData.set('quantity', '1');
  try {
    const result = await nvAddToCart(formData, button);
    if (!result.error) {
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1600);
    }
  } finally {
    button.removeAttribute('aria-busy');
  }
});

/** Tabs that switch between product panels. */
class NvTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab)));
  }

  select(tab) {
    this.tabs.forEach((other) => {
      const active = other === tab;
      other.setAttribute('aria-selected', String(active));
      const panel = this.querySelector(`#${other.getAttribute('aria-controls')}`);
      if (panel) panel.hidden = !active;
      if (active && panel) panel.querySelectorAll('nv-carousel').forEach((c) => c.update && c.update());
    });
  }
}

/**
 * Nuriva cart: drawer + cart page share the same markup (snippets/nv-cart-contents.liquid).
 * Re-rendered through the Section Rendering API after every cart change.
 */
const nvCart = {
  roots() {
    return [...document.querySelectorAll('[data-cart-root]')];
  },

  async refresh() {
    const roots = this.roots();
    const ids = [...new Set(roots.map((root) => root.closest('[data-section-id]')?.dataset.sectionId).filter(Boolean))];
    if (!ids.length) return;
    const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
    if (!response.ok) return;
    const html = await response.json();
    roots.forEach((root) => {
      const id = root.closest('[data-section-id]')?.dataset.sectionId;
      if (!id || !html[id]) return;
      const doc = new DOMParser().parseFromString(html[id], 'text/html');
      const fresh = doc.querySelector('[data-cart-root]');
      if (fresh) root.innerHTML = fresh.innerHTML;
    });
    this.localiseShipping();
  },

  localiseShipping() {
    document.querySelectorAll('[data-cart-contents]').forEach((el) => {
      const rate = Number(window.Shopify?.currency?.rate) || 1;
      const currency = window.Shopify?.currency?.active || 'USD';
      // UK and Canada thresholds are already in local currency
      const threshold = el.hasAttribute('data-threshold-local') ? Number(el.dataset.threshold) : Math.round(Number(el.dataset.threshold) * rate);
      const total = Number(el.dataset.total) || 0;
      const text = el.querySelector('[data-free-shipping-text]');
      const bar = el.querySelector('[data-free-shipping-bar]');
      if (!text || !threshold) return;
      // a fully subscribed cart ships free, whatever the total
      if (el.dataset.subFree) {
        text.textContent = el.dataset.subFree;
        if (bar) bar.style.width = '100%';
        return;
      }
      if (total >= threshold) {
        text.textContent = el.dataset.done;
      } else {
        let amount;
        try {
          amount = nvRound(formatMoney(threshold - total, el.dataset.moneyFormat || '${{amount}}', currency));
        } catch (error) {
          amount = nvRound(((threshold - total) / 100).toFixed(2));
        }
        text.textContent = el.dataset.left.replace('[amount]', amount);
      }
      if (bar) bar.style.width = `${Math.min(100, (total / threshold) * 100)}%`;
    });
  },

  /**
   * Switch every one-time line that has a plan to its first (monthly) plan.
   * Shopify gives every line a new key after each change, so the next line is looked up in the returned cart.
   */
  async subscribeAll(source) {
    const plans = new Map();
    document.querySelectorAll('[data-cart-contents] [data-line][data-plan]').forEach((line) =>
      plans.set(Number(line.dataset.variant), Number(line.dataset.plan))
    );
    source.setAttribute('aria-busy', 'true');
    let cart = await fetch('/cart.js').then((res) => res.json()).catch(() => undefined);
    for (let step = 0; cart && step < 20; step++) {
      const index = cart.items.findIndex((item) => !item.selling_plan_allocation && plans.has(item.variant_id));
      if (index < 0) break;
      const item = cart.items[index];
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: index + 1, quantity: item.quantity, selling_plan: plans.get(item.variant_id) }),
      });
      if (!response.ok) break;
      cart = await response.json();
    }
    source.removeAttribute('aria-busy');
    if (cart) document.dispatchEvent(new CartUpdateEvent(cart, 'nv-cart', { source: 'nv-cart', itemCount: cart.item_count }));
  },

  async change(line, quantity, source, sellingPlan) {
    source.closest('.nv-cart-item')?.setAttribute('aria-busy', 'true');
    const body = { line, quantity };
    // selling_plan: an id switches the line to that subscription, null switches it back to one-time
    if (sellingPlan !== undefined) body.selling_plan = sellingPlan;
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const cart = await response.json();
    document.dispatchEvent(new CartUpdateEvent(cart, 'nv-cart', { source: 'nv-cart', itemCount: cart.item_count }));
  },
};

class NvCartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog?.addEventListener('close', () => document.documentElement.style.removeProperty('overflow'));
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
  }

  close() {
    this.dialog?.close();
  }
}

document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
  const source = event.detail?.data?.source;
  await nvCart.refresh();
  if (source !== 'nv-cart' && !event.detail?.data?.didError) document.querySelector('nv-cart-drawer')?.open();
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const qty = target.closest('[data-cart-qty]');
  if (qty) {
    event.preventDefault();
    const line = Number(qty.closest('[data-line]')?.dataset.line);
    if (line) nvCart.change(line, Number(qty.dataset.cartQty), qty);
    return;
  }
  const subscribeAll = target.closest('[data-cart-subscribe-all]');
  if (subscribeAll) {
    event.preventDefault();
    if (subscribeAll.getAttribute('aria-busy') !== 'true') nvCart.subscribeAll(subscribeAll);
    return;
  }
  const plan = target.closest('[data-cart-plan]');
  if (plan) {
    event.preventDefault();
    const item = plan.closest('[data-line]');
    const line = Number(item?.dataset.line);
    const value = plan.dataset.cartPlan;
    if (line) nvCart.change(line, Number(item.dataset.quantity) || 1, plan, value ? Number(value) : null);
    return;
  }
  if (target.closest('[data-cart-close]')) {
    document.querySelector('nv-cart-drawer')?.close();
    return;
  }
  const link = target.closest('a[href$="/cart"], a[href*="/cart?"]');
  const drawer = document.querySelector('nv-cart-drawer');
  if (link && drawer && !link.closest('[data-cart-root]')) {
    event.preventDefault();
    drawer.open();
  }
});

document.addEventListener('DOMContentLoaded', () => nvCart.localiseShipping());

if (!customElements.get('nv-carousel')) customElements.define('nv-carousel', NvCarousel);
if (!customElements.get('nv-product')) customElements.define('nv-product', NvProduct);
if (!customElements.get('nv-quiz')) customElements.define('nv-quiz', NvQuiz);
if (!customElements.get('nv-tabs')) customElements.define('nv-tabs', NvTabs);
if (!customElements.get('nv-cart-drawer')) customElements.define('nv-cart-drawer', NvCartDrawer);
nvCart.localiseShipping();

/* Offer badges, pills and notes hide themselves once the offer has ended, even on a cached page. */
document.querySelectorAll('[data-promo-ends]').forEach((element) => {
  const ends = Date.parse(element.dataset.promoEnds || '');
  if (!Number.isNaN(ends) && Date.now() >= ends && !element.matches('nv-product, nv-quiz')) element.hidden = true;
});
